"use client"

import clsx from "clsx"
import { Loader2 } from "lucide-react"
import React from "react"
import { useDataAttributes } from "../../hooks"

/**
 * @description TMenu 내부의 메뉴 아이템. 아이콘(prefix), 단축키(suffix), 파괴적 액션(destructive) 지원
 * @example
 * <TMenuItem label="편집" prefix={<TIcon name="edit" size={16} />} />
 * <TMenuItem label="삭제" destructive prefix={<TIcon name="delete" size={16} />} />
 * <TMenuItem label="복사" suffix={<span>Ctrl+C</span>} />
 */
interface TMenuItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "prefix"> {
	/** 메뉴 항목 텍스트 또는 커스텀 노드 */
	label: string | React.ReactNode
	/** 아이콘 등 앞부분 요소 */
	prefix?: React.ReactNode
	/** 단축키 표시 등 뒷부분 요소 */
	suffix?: React.ReactNode
	/** 비활성화 */
	disabled?: boolean
	/** 로딩 상태 (스피너 표시) */
	loading?: boolean
	/** 선택 상태 */
	selected?: boolean
	/** true 시 빨간색 텍스트로 표시 (삭제 등 파괴적 액션) */
	destructive?: boolean
	/** @deprecated data-* 속성을 직접 사용하세요 */
	dataAttributes?: Record<string, string | number | boolean>
}

export const TMenuItem = React.memo((props: TMenuItemProps) => {
	const { label, className, prefix, suffix, disabled, loading, selected, destructive, dataAttributes, onClick, ...restProps } = props
	const dataProps = useDataAttributes(dataAttributes)

	return (
		<div
			className={clsx("TMenuItem", className)}
			{...restProps}
			data-disabled={disabled}
			data-loading={loading}
			data-selected={selected}
			data-destructive={destructive}
			{...dataProps}
			onClick={onClick}
		>
			{loading && (
				<span className="loader">
					<Loader2 aria-hidden="true" />
				</span>
			)}
			{prefix && !loading && prefix}
			<span className="label">{label}</span>
			{suffix && suffix}
		</div>
	)
})

TMenuItem.displayName = "TMenuItem"
